import { Component, inject } from '@angular/core';
import { DeviceService } from './common/services/device.service';
import { SignalREventService, SignalRHubService } from './common/services/signalR.service';
import { EMPTY, filter, firstValueFrom, from, skip, switchMap, take, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LocationValidationStatus, SecureDataService, SecureDataStatusModel, SecureDataStatusResponse } from './common/services/securedata.sevice';
import { BaseViewComponent } from './feature/base-view.component';
import { HttpErrorResponse } from '@angular/common/http';
import { StateChangedModel } from './common/services/state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as Sentry from '@sentry/angular';
import { LocationPermissionStatus } from './common/services/location.service';




@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent extends BaseViewComponent {

  title = 'saraplus-securedata';
  #deviceService = inject(DeviceService);
  #signalRHubService: SignalRHubService = inject(SignalRHubService);
  #secureDataService = inject(SecureDataService);
  #route = inject(ActivatedRoute);
  #signalREventService = inject(SignalREventService);
  currentStateChangedModel: StateChangedModel | undefined;
  readonly locationState = this.#deviceService.locationState;

  /**
   * On Angular Init Check if query params (id & t) available if not then navigate to 404 
   * else start the secure data process
   *
   * @memberof AppComponent
   */

  protected override refreshView() {
    this.spinnerService.loadingSub.subscribe({
      next: (value) => {
        this.showLoader = value
      },
    })
    this.#route.queryParams
      .pipe(
        skip(1),
        take(1),
        tap(params => {
          this.stateService.ConnectionId = params['id'];
          this.stateService.MethodType = params['t'];
        }),
        switchMap(() => {

          const connectionId =
            this.stateService.ConnectionId;

          const methodType =
            this.stateService.MethodType;

          if (!connectionId || !methodType) {
            this.router.navigate(['404']);
            return EMPTY;
          }

          return from(this.getStatus());
        })
      )
      .subscribe();

    this.#signalREventService.Events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event?.id !== undefined),
        switchMap(() => from(this.getStatus()))
      )
      .subscribe();

    this.#signalRHubService.Resync
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => from(this.resyncStatus()))
      )
      .subscribe();
  }

  /**
   * Call the Current Status API and if success start signalR connection
   */

  async getStatus(): Promise<void> {
    this.spinnerService.setLoading(true, '@AppComponent.getStatus');
    try {
      await this.#deviceService.initDevice();
      const data = await firstValueFrom(this.#secureDataService.getStatus());
      this.#signalRHubService.startConnection();
      await this.routeToStatus(data);

    } catch (err) {
      const error = err as HttpErrorResponse;
      if (error.status === 410) {
        this.navigateExpired();
      } else {
        this.notFound();
      }
    }
    finally {
      this.spinnerService.setLoading(false, '@AppComponent.getStatus');
    }
  }

  /**
   * The hub is back after a drop or a failed start. Stage pushes sent in the gap are lost, so re-read
   * getcurrent and move on only when the stage changed: re-applying the same stage would re-patch
   * the form the customer may be filling in.
   */
  async resyncStatus(): Promise<void> {
    const previous = this.stateService.CurrentStatus;
    try {
      const data = await firstValueFrom(this.#secureDataService.getStatus(false));
      if (data && previous && data.Id === previous.Id && data.Type === previous.Type) {
        return;
      }
      Sentry.addBreadcrumb({ category: 'signalr', message: `resync moved stage to ${data?.Type ?? 'waiting'}`, level: 'info' });
      if (data) {
        this.stateService.CurrentStatus = data;
      }
      await this.routeToStatus(data);
    } catch (err) {
      // Not fatal: the customer stays where they are and the next push or a reload brings them on
      console.warn('SignalR status resync failed:', err);
      if ((err as HttpErrorResponse)?.status === 410) {
        this.navigateExpired();
      }
    }
  }

  private async routeToStatus(data: SecureDataStatusResponse | null | undefined): Promise<void> {
    if (!data) {
      this.navigateWaiting();
      return;
    }
    const locationStatus = this.resolveLocationStatus(data);
    this.initSentryInfo(data, locationStatus);
    if (locationStatus !== 'Allowed' && this.locationState()?.status !== LocationPermissionStatus.Granted) {
      await this.navigateLocationRequired();
      return;
    }

    this.navigateToSecureDataType(data.Type);
  }

  initSentryInfo(data: SecureDataStatusResponse | null | undefined, locationStatus: LocationValidationStatus): void {
    const locationPayload: SecureDataStatusModel = this.#secureDataService.getSecureDataStatusModel();
    Sentry.setTag('OrderNumber', data?.OrderNumber ?? 'unknown');
    Sentry.setTag('UniqueId', locationPayload?.UniqueId ?? 'unknown');

    Sentry.addBreadcrumb({
      category: 'secure-data-location',
      message: 'ip_location_info',
      data: {
        OrderNumber: data?.OrderNumber ?? 'unknown',
        UniqueId: locationPayload?.UniqueId ?? 'unknown',
        Latitude: locationPayload?.Latitude ?? 'unknown',
        Longitude: locationPayload?.Longitude ?? 'unknown',
        LocationServicesEnabled: locationPayload?.LocationServicesEnabled ?? 'unknown',
        GeoAccuracy: locationPayload?.GeoAccuracy ?? 'unknown',
        LocationStatus: locationStatus
      },
      level: 'info'
    });
  }

  private resolveLocationStatus(data: SecureDataStatusResponse | null | undefined): LocationValidationStatus {
    if (!data || !data.LocationValidationStatus) {
      return 'OutsideUnitedStates';
    }

    return data.LocationValidationStatus;
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    this.currentStateChangedModel = val;
  }
}
